import { api } from './client'
import type { User } from '../types'

// In-flight dedup + 60s TTL cache for user profiles
const _profileCache = new Map<string, { promise: Promise<User>; ts: number }>()
function profileCache(userId: string): Promise<User> {
  const now = Date.now()
  const cached = _profileCache.get(userId)
  if (cached && now - cached.ts < 60_000) return cached.promise
  const promise = api.get<User>(`/v1/users/${userId}`)
  _profileCache.set(userId, { promise, ts: now })
  promise.catch(() => _profileCache.delete(userId))
  return promise
}

export interface PrivacySettings {
  phone_visibility: string
  avatar_visibility: string
  last_seen_visibility: string
  who_can_add_to_groups: string
  who_can_message: string
}

export const usersApi = {
  getMe: (): Promise<User> =>
    api.get('/v1/users/me'),

  updateProfile: (data: {
    username?: string
    first_name?: string
    last_name?: string
    bio?: string
  }): Promise<User> =>
    api.patch('/v1/users/me', data),

  setAvatar: (avatarUrl: string): Promise<User> =>
    api.put('/v1/users/me/avatar', { avatar_url: avatarUrl }),

  deleteAvatar: (): Promise<void> =>
    api.delete('/v1/users/me/avatar'),

  search: (q: string): Promise<{ users: User[] }> =>
    api.get(`/v1/users/search?q=${encodeURIComponent(q)}`),

  getProfile: (userId: string): Promise<User> => profileCache(userId),

  getPrivacy: (): Promise<PrivacySettings> =>
    api.get('/v1/users/me/privacy'),

  updatePrivacy: (data: Partial<PrivacySettings>): Promise<PrivacySettings> =>
    api.patch('/v1/users/me/privacy', data),

  blockUser: (userId: string) =>
    api.post(`/v1/users/me/block/${userId}`, {}),

  unblockUser: (userId: string) =>
    api.delete(`/v1/users/me/block/${userId}`),

  getBlocked: (): Promise<{ blocked: User[] }> =>
    api.get('/v1/users/me/blocked'),

  listContacts: (): Promise<{ contacts: User[] }> =>
    api.get('/v1/users/me/contacts'),

  addContact: (userId: string): Promise<void> =>
    api.post('/v1/users/me/contacts', { user_id: userId }),

  removeContact: (userId: string): Promise<void> =>
    api.delete(`/v1/users/me/contacts/${userId}`),
}
